import { useNavigate } from '@share/utils';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { ACTIVITY_RECENT_SEARCHES_LABEL, Routes } from '@share/constants';
import { computeIsRecentSearchPreviewImagesDisabled, getRecentSearchLocationKeyHotel, GetHomeParams, RootState, AppDispatch } from '@share/utils';

import { activityLocationActions, datesActions, roomsActions, getActivity } from '@share/store/slices';
import { v4 } from 'uuid';

import type { IRoom } from '@share/common-types';
import { NoImageSvg } from '@assets';

import './styles.scss';

/** Activities price by adult age; recent searches do not store them. */
const DEFAULT_ADULT_AGE = 18;

const MAX_RECENT_DISPLAY = 4;

export interface IStoredRecentSearch {
  startDate: string;
  location: { name?: string; code?: string; [key: string]: any };
  locationCode?: string;
  occupancy: { adultsCount: number; kids: number[] };
  /** Preview image URLs from the first successful search results (first image per activity, up to 6). */
  images?: string[];
}

function getRecentSearchesFromStorage(): IStoredRecentSearch[] {
  try {
    const raw = localStorage.getItem(ACTIVITY_RECENT_SEARCHES_LABEL);
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    const todayStart = moment().startOf('day');
    const filtered = list
      .filter(
        (item: any) =>
          item?.startDate && moment(item.startDate, 'YYYY-MM-DD').isSameOrAfter(todayStart, 'day'),
      )
      .map((item: any) => ({
        ...item,
        occupancy: normalizeOccupancy(item.occupancy ?? item.rooms),
      }));
    const seenKeys = new Set<string>();
    const deduped: IStoredRecentSearch[] = [];
    for (const item of filtered) {
      const key = getRecentSearchLocationKeyHotel(item);
      if (!key || seenKeys.has(key)) continue;
      seenKeys.add(key);
      deduped.push(item);
    }
    return deduped;
  } catch {
    return [];
  }
}

function formatDate(startDate: string): string {
  const m = moment(startDate, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss']);
  return m.isValid() ? m.format('D MMM YYYY') : startDate;
}

function normalizeOccupancy(value: any): IStoredRecentSearch['occupancy'] {
  if (value && typeof value === 'object' && !Array.isArray(value) && typeof value.adultsCount === 'number') {
    return {
      adultsCount: value.adultsCount,
      kids: Array.isArray(value.kids) ? value.kids : [],
    };
  }
  if (Array.isArray(value) && value.length) {
    const adultsCount = value.reduce((s: number, r: any) => s + (r?.adultsCount ?? 0), 0);
    const kids = value.flatMap((r: any) => (Array.isArray(r?.kids) ? r.kids : []));
    return { adultsCount: adultsCount || 1, kids };
  }
  return { adultsCount: 1, kids: [] };
}

const PREVIEW_IMAGE_MAX = 4;

function RecentSearchPreviewGrid({ urls }: { urls: string[] }) {
  const images = urls.slice(0, PREVIEW_IMAGE_MAX).filter(Boolean);
  const count = images.length;

  const countClass =
    count <= 1
      ? 'recent-searches__preview--1'
      : count === 2
        ? 'recent-searches__preview--2'
        : count === 3
          ? 'recent-searches__preview--3'
          : 'recent-searches__preview--4';

  return (
    <div className={`activity-recent-searches__preview ${countClass}`} aria-hidden>
      {count === 0 ? (
        <div className="activity-recent-searches__preview-cell activity-recent-searches__preview-cell--default">
          <NoImageSvg />
        </div>
      ) : (
        images.map((src, i) => (
          <div key={`${src}-${i}`} className="activity-recent-searches__preview-cell">
            <img src={src} alt="" loading="lazy" />
          </div>
        ))
      )}
    </div>
  );
}

function formatOccupancy(occupancy: IStoredRecentSearch['occupancy']): string {
  const adults = occupancy?.adultsCount ?? 0;
  const kids = occupancy?.kids?.length ?? 0;

  const parts: string[] = [];
  if (adults) {
    parts.push(`${adults} Adult${adults !== 1 ? 's' : ''}`);
  }
  if (kids) {
    parts.push(`${kids} Child${kids !== 1 ? 'ren' : ''}`);
  }
  
  return parts.length ? parts.join(', ') : '—';
}

const RecentSearches: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const account = useSelector((state: RootState) => state.loginStore?.account);
  const navigationItems = useSelector((state: RootState) => state.navigationMenuStore?.items);
  const accountName = account?.name;

  const showRecentSearchPreviewImages = !computeIsRecentSearchPreviewImagesDisabled(
    account,
    navigationItems?.siteId,
    navigationItems?.organizationId,
  );

  const recentList = useMemo(() => {
    const list = getRecentSearchesFromStorage();
    return list.slice(0, MAX_RECENT_DISPLAY);
  }, []);

  const handleSearchAgain = (item: IStoredRecentSearch) => {
    const location = item?.location;
    if (!location) return;

    dispatch(activityLocationActions.setSelectLocation(location as any));
    dispatch(activityLocationActions.setSelectLocationLabel(location.name ?? ''));

    const startDate = item.startDate?.split('T')[0] || item.startDate;
    if (startDate) {
      // Activities are single-day, so both ends of the shared range are that day.
      // Leaving the end off makes `isDataValid` reject the search; upstream gets away
      // with it because its own dates slice mirrors `startDate` when the end is absent.
      dispatch(datesActions.setDatesSelected({ startDate, endDate: startDate }));
    }

    if (item.occupancy && typeof item.occupancy.adultsCount === 'number') {
      const { adultsCount, kids } = item.occupancy;
      const adults = adultsCount ?? 1;

      // Stored recent searches keep kid ages as plain numbers; IRoom wants IGuest.
      const rooms: IRoom[] = [{
        id: v4(),
        adultsCount: adults,
        adultAges: Array(adults).fill(DEFAULT_ADULT_AGE),
        kids: (Array.isArray(kids) ? kids : []).map((age: number) => ({ id: v4(), age })),
      }];

      dispatch(roomsActions.setRooms(rooms));
    }

    const searchPath = `${accountName ? `/${accountName}${Routes.ActivitiesSearch}` : Routes.ActivitiesSearch}${GetHomeParams(account)}`;
    navigate(searchPath);
    dispatch(
      getActivity({
        lastSearchedLocation: location as any,
        isLoadMore: false,
        isFiltersChange: false,
        isReuseSessionKey: false,
      })
    );
  };

  if (!recentList.length) return null;

  return (
    <section className="activity-recent-searches">
      <h2 className="activity-recent-searches__title">Your recent searches</h2>
      <div className="activity-recent-searches__list">
        {recentList.map((item, index) => {
          const previewUrls = item.images ?? [];

          return (
            <div
              key={`${item.locationCode}-${item.startDate}-${index}`}
              className={`activity-recent-searches__item${showRecentSearchPreviewImages ? ' activity-recent-searches__item--with-preview' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => handleSearchAgain(item)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchAgain(item)}
            >
              {showRecentSearchPreviewImages ? <RecentSearchPreviewGrid urls={previewUrls} /> : null}
              <div className="activity-recent-searches__item-body">
                <div className="activity-recent-searches__item-content">
                  <div className="activity-recent-searches__item-location">
                    <FontAwesomeIcon icon={faLocationDot} className="activity-recent-searches__item-icon" />
                    <span>{item.location?.name ?? item.location?.displayName ?? '—'}</span>
                  </div>
                  <div className="activity-recent-searches__item-meta">
                    <span className="activity-recent-searches__item-dates">{formatDate(item.startDate)}</span>
                    <span className="activity-recent-searches__item-occupancy">{formatOccupancy(item.occupancy)}</span>
                  </div>
                </div>
                <span className="activity-recent-searches__item-action">
                  Search again
                  <FontAwesomeIcon icon={faChevronRight} className="activity-recent-searches__item-arrow" />
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default RecentSearches;
